import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, Mail, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [email, setEmail] = useState('');
  const [secretCode, setSecretCode] = useState('');
  const [showCode, setShowCode] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !secretCode) {
      toast.error('Remplissez tous les champs');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, {
        email,
        secret_code: secretCode
      });
      
      // Store token
      localStorage.setItem('token', response.data.access_token);
      // Nettoyer les tokens partenaires résiduels pour éviter les conflits
      localStorage.removeItem('partner_token');
      localStorage.removeItem('partner_data');
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      
      // Refresh user context to update auth state
      await refreshUser();
      
      toast.success(response.data.message || 'Connexion réussie !');
      
      // Redirect to admin dashboard
      navigate('/admin/dashboard');
      
    } catch (error) {
      console.error('Admin login error:', error);
      if (error.response?.status === 403) {
        toast.error('Code secret invalide');
      } else {
        toast.error('Erreur de connexion');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy flex flex-col">
      {/* Header */}
      <header className="py-6 px-6 border-b border-white/10">
        <div className="max-w-4xl mx-auto">
          <h1 
            className="font-heading text-xl font-bold text-white cursor-pointer"
            onClick={() => navigate('/')}
          >
            L'ŒIL DU MARCHAND
          </h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Card className="border-0 shadow-2xl bg-white/5 backdrop-blur">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gold/20 mb-4">
                  <Shield className="w-10 h-10 text-gold" />
                </div>
                <h2 className="font-heading text-2xl font-bold text-white">
                  Accès Propriétaire
                </h2>
                <p className="text-white/60 mt-2">
                  Espace réservé au gestionnaire de l'application
                </p>
              </div>

              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <Label htmlFor="admin-email" className="text-white/80">Email Administrateur</Label>
                  <div className="relative mt-2">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                    <Input
                      id="admin-email"
                      data-testid="admin-email-input"
                      type="email"
                      placeholder="votre@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-gold focus:ring-gold"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="secret-code" className="text-white/80">Code Secret</Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                    <Input
                      id="secret-code"
                      data-testid="admin-secret-input"
                      type={showCode ? "text" : "password"}
                      placeholder="••••••••••••"
                      value={secretCode}
                      onChange={(e) => setSecretCode(e.target.value)}
                      className="pl-10 pr-10 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-gold focus:ring-gold"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowCode(!showCode)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gold hover:text-gold/80 transition-colors z-10 outline-none focus:outline-none border-none bg-transparent p-0"
                      data-testid="toggle-secret-code-btn"
                    >
                      {showCode ? <EyeOff className="w-6 h-6" /> : <Eye className="w-6 h-6" />}
                    </button>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  data-testid="admin-login-btn"
                  disabled={loading}
                  className="w-full h-12 bg-gold hover:bg-gold/90 text-black font-bold text-lg"
                >
                  {loading ? (
                    'Connexion...'
                  ) : (
                    <>
                      Accéder au Dashboard
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
                
                {/* Mot de passe oublié */}
                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={() => navigate('/admin/reset-password')}
                    className="text-gold/70 hover:text-gold text-sm underline"
                  >
                    Code secret oublié ?
                  </button>
                </div>
              </form>

              <div className="mt-8 pt-6 border-t border-white/10 text-center">
                <p className="text-xs text-white/40">
                  Cet accès est réservé au propriétaire de l'application.
                  <br />
                  Toute tentative non autorisée sera enregistrée.
                </p>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-white/40 mt-6">
            <button onClick={() => navigate('/auth')} className="text-gold hover:underline">
              Retour à la connexion client
            </button>
          </p>
        </div>
      </main>
    </div>
  );
}
